<template>
  <div class="article-page">
    <!-- 面包屑 -->
    <nav class="breadcrumb-nav">
      <router-link to="/">首页</router-link>
      <span class="separator">/</span>
      <router-link to="/research">研究中心</router-link>
      <span class="separator">/</span>
      <span class="current">制造业数字化转型的五大挑战与破局之道</span>
    </nav>

    <article class="container">
      <!-- 头部 -->
      <header class="article-header">
        <div class="article-tags">
          <span class="tag">行业观察</span>
          <span class="tag">制造行业</span>
          <span class="tag">数字化转型</span>
        </div>
        <h1 class="article-title">制造业数字化转型的五大挑战与破局之道</h1>
        <p class="article-subtitle">一汽集团 · 中船重工 · 中国中车 · 深度拆解关键难点与破局策略</p>
        <div class="article-meta">
          <span><i class="far fa-calendar-alt"></i> 2025-12-18</span>
          <span><i class="far fa-clock"></i> 阅读约 16 分钟</span>
          <span><i class="far fa-user"></i> 古杉研究中心</span>
        </div>
      </header>

      <!-- 导读 -->
      <section class="intro-card">
        <div class="intro-icon">🏭</div>
        <div>
          <h3>导读</h3>
          <p>制造业是国民经济的主体，也是数字化转型复杂度最高的行业之一。本文基于对一汽集团、中船重工、中国中车等制造业央企龙头的深度调研，系统梳理制造业数字化转型面临的五大核心挑战，并给出经过验证的破局策略与落地路径。</p>
        </div>
      </section>

      <!-- 目录 -->
      <nav class="toc">
        <h3>📑 目录</h3>
        <ul>
          <li><a href="#section1">一、制造业数字化转型现状</a></li>
          <li><a href="#section2">二、挑战一：OT与IT深度融合之难</a></li>
          <li><a href="#section3">三、挑战二：数据孤岛与数据质量顽疾</a></li>
          <li><a href="#section4">四、挑战三：供应链协同效率低下</a></li>
          <li><a href="#section5">五、挑战四：人才缺口与组织惯性</a></li>
          <li><a href="#section6">六、挑战五：投入产出难以量化</a></li>
          <li><a href="#section7">七、标杆案例与方法论总结</a></li>
        </ul>
      </nav>

      <!-- 正文 -->
      <div class="article-body">

        <!-- 第一章 -->
        <section id="section1" class="chapter">
          <h2>一、制造业数字化转型现状</h2>

          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-number">68%</div>
              <div class="stat-label">的制造业央企已启动数字化转型</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">仅23%</div>
              <div class="stat-label">进入规模化推广阶段</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">2.1%</div>
              <div class="stat-label">制造业平均数字化投入占营收比</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">55%</div>
              <div class="stat-label">的转型项目未达到预期目标</div>
            </div>
          </div>

          <p>制造业数字化转型有别于金融、互联网等行业，其核心难点在于：<strong>物理世界与数字世界的深度融合</strong>。工厂里的产线、设备、物料、工艺构成了复杂的物理系统，如何将这些"看得见摸得着"的要素数字化，是制造业独特的挑战。</p>

          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">⚙️</div>
              <div>
                <h4>制造业数字化的本质</h4>
                <p>用数据打通"研发-生产-供应链-销售-服务"全价值链，实现从"经验驱动"到"数据驱动"的根本转变。</p>
              </div>
            </div>
          </div>

          <div class="comparison-table">
            <h4>制造业 vs 其他行业数字化转型对比</h4>
            <table>
              <thead>
                <tr>
                  <th>维度</th>
                  <th>制造业</th>
                  <th>金融业</th>
                  <th>零售业</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>数据来源</td>
                  <td>设备传感器+MES+ERP</td>
                  <td>交易系统+客户数据</td>
                  <td>POS+电商+会员</td>
                </tr>
                <tr>
                  <td>核心难度</td>
                  <td>OT/IT融合</td>
                  <td>实时风控</td>
                  <td>全渠道整合</td>
                </tr>
                <tr>
                  <td>转型周期</td>
                  <td>5-8年</td>
                  <td>3-5年</td>
                  <td>2-4年</td>
                </tr>
                <tr>
                  <td>投入强度</td>
                  <td>高（含硬件改造）</td>
                  <td>中高</td>
                  <td>中</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- 第二章 挑战一 -->
        <section id="section2" class="chapter">
          <h2>二、挑战一：OT与IT深度融合之难</h2>

          <div class="challenge-banner">
            <div class="challenge-number">01</div>
            <div>
              <h3>工业技术（OT）与信息技术（IT）的融合是制造业最大的技术瓶颈</h3>
              <p class="challenge-stat">82% 的制造业CIO认为OT/IT融合是最大难题</p>
            </div>
          </div>

          <h3>问题剖析</h3>
          <div class="problem-grid">
            <div class="problem-card">
              <div class="problem-icon">🔧</div>
              <h4>协议不统一</h4>
              <p>工厂设备采用Modbus、OPC UA、Profinet等上百种工业协议，数据采集和互通极其困难。一个工厂可能有20+种设备通信协议。</p>
            </div>
            <div class="problem-card">
              <div class="problem-icon">⏱️</div>
              <h4>实时性要求极高</h4>
              <p>产线控制要求毫秒级响应，而IT系统通常是秒级甚至分钟级，两者对时延的要求差距达1000倍。</p>
            </div>
            <div class="problem-card">
              <div class="problem-icon">🛡️</div>
              <h4>安全边界模糊</h4>
              <p>OT网络一旦被攻击可能导致物理设备失控，后果远超IT系统。但传统IT安全方案无法直接匹配OT环境。</p>
            </div>
            <div class="problem-card">
              <div class="problem-icon">👥</div>
              <h4>团队文化冲突</h4>
              <p>OT工程师和IT工程师的知识体系、工作方式、考核标准完全不同，协作沟通成本极高。</p>
            </div>
          </div>

          <h3>破局之道</h3>
          <div class="solution-steps">
            <div class="solution-step" v-for="(step, i) in otItSolutions" :key="i">
              <div class="step-marker">{{ i + 1 }}</div>
              <div class="step-content">
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
              </div>
            </div>
          </div>

          <div class="case-mini">
            <div class="case-mini-header">💡 一汽集团实践</div>
            <p>一汽集团通过部署统一工业数据采集平台（基于OPC UA + MQTT协议桥接），实现了8个工厂、2000+台设备的数据统一采集与上云。产线数据采集频率从分钟级提升至100ms级，设备综合效率（OEE）提升18%。</p>
          </div>
        </section>

        <!-- 第三章 挑战二 -->
        <section id="section3" class="chapter">
          <h2>三、挑战二：数据孤岛与数据质量顽疾</h2>

          <div class="challenge-banner">
            <div class="challenge-number">02</div>
            <div>
              <h3>制造业的数据孤岛问题比任何行业都更严重</h3>
              <p class="challenge-stat">平均一家制造业央企拥有 150+ 个独立信息系统</p>
            </div>
          </div>

          <h3>问题剖析</h3>
          <div class="island-diagram">
            <div class="island-layer">
              <div class="island-title">研发层</div>
              <div class="island-systems">
                <span>PLM</span><span>CAD/CAE</span><span>PDM</span>
              </div>
            </div>
            <div class="island-gap">
              <span class="gap-label">⚠️ 数据断层</span>
            </div>
            <div class="island-layer">
              <div class="island-title">生产层</div>
              <div class="island-systems">
                <span>MES</span><span>SCADA</span><span>DCS</span><span>PLC</span>
              </div>
            </div>
            <div class="island-gap">
              <span class="gap-label">⚠️ 数据断层</span>
            </div>
            <div class="island-layer">
              <div class="island-title">经营层</div>
              <div class="island-systems">
                <span>ERP</span><span>SRM</span><span>CRM</span><span>OA</span>
              </div>
            </div>
          </div>

          <div class="data-quality-section">
            <h3>数据质量问题清单</h3>
            <div class="quality-grid">
              <div class="quality-card" v-for="item in dataQualityIssues" :key="item.title">
                <div class="quality-icon" :style="{ color: item.color }">{{ item.icon }}</div>
                <h4>{{ item.title }}</h4>
                <p>{{ item.desc }}</p>
                <div class="quality-impact">影响：{{ item.impact }}</div>
              </div>
            </div>
          </div>

          <h3>破局之道</h3>
          <div class="pillar-section">
            <div class="pillar-card">
              <div class="pillar-icon">📊</div>
              <h4>建设工业数据中台</h4>
              <p>统一数据标准、统一数据模型、统一数据服务</p>
              <ul>
                <li>物料主数据统一编码</li>
                <li>设备档案标准化</li>
                <li>工艺参数字典建设</li>
              </ul>
            </div>
            <div class="pillar-card">
              <div class="pillar-icon">🔄</div>
              <h4>实施数据治理体系</h4>
              <p>数据标准、数据质量、数据安全三管齐下</p>
              <ul>
                <li>DCMM成熟度评估</li>
                <li>数据质量六维度监控</li>
                <li>数据责任人制度</li>
              </ul>
            </div>
            <div class="pillar-card">
              <div class="pillar-icon">🔗</div>
              <h4>打通系统集成链路</h4>
              <p>通过API网关和iPaaS实现系统互通</p>
              <ul>
                <li>PLM-ERP-MES集成</li>
                <li>实时数据流水线</li>
                <li>统一数据资产目录</li>
              </ul>
            </div>
          </div>

          <div class="case-mini">
            <div class="case-mini-header">💡 中船重工实践</div>
            <p>中船重工集团通过"一数一源"治理策略，用18个月完成了船舶制造核心主数据（物料、BOM、工艺路线、设备档案）的统一治理。数据准确率从62%提升至94%，BOM变更传递时间从3天缩短至2小时，产品交付周期缩短15%。</p>
          </div>
        </section>

        <!-- 第四章 挑战三 -->
        <section id="section4" class="chapter">
          <h2>四、挑战三：供应链协同效率低下</h2>

          <div class="challenge-banner">
            <div class="challenge-number">03</div>
            <div>
              <h3>制造业供应链的"牛鞭效应"在数字化不足时被极度放大</h3>
              <p class="challenge-stat">库存周转天数平均比全球标杆企业高出 40%</p>
            </div>
          </div>

          <h3>问题剖析</h3>
          <p>制造业供应链涉及原材料采购、零部件加工、总装集成、物流配送、售后服务等多个环节。核心痛点集中在三个方面：</p>

          <div class="pain-points">
            <div class="pain-card">
              <div class="pain-header red">
                <span>痛点一</span>
              </div>
              <div class="pain-body">
                <h4>需求预测不准</h4>
                <p>市场端需求波动传递到生产端被逐级放大，导致库存积压与缺料并存。某汽车零部件企业呆滞库存占比高达18%，同时急件采购占比12%。</p>
              </div>
            </div>
            <div class="pain-card">
              <div class="pain-header orange">
                <span>痛点二</span>
              </div>
              <div class="pain-body">
                <h4>供应商协同困难</h4>
                <p>核心制造企业的一级供应商通常有200-500家，供应商排产计划、质量数据、物流信息交换仍以电话、邮件为主。</p>
              </div>
            </div>
            <div class="pain-card">
              <div class="pain-header yellow">
                <span>痛点三</span>
              </div>
              <div class="pain-body">
                <h4>质量追溯链条断裂</h4>
                <p>从原材料到成品的全链条追溯数据不完整，一旦出现质量问题，召回范围过大、定位速度过慢。</p>
              </div>
            </div>
          </div>

          <h3>破局之道</h3>
          <div class="solution-steps">
            <div class="solution-step" v-for="(step, i) in supplySolutions" :key="i">
              <div class="step-marker">{{ i + 1 }}</div>
              <div class="step-content">
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
              </div>
            </div>
          </div>

          <div class="case-mini">
            <div class="case-mini-header">💡 中国中车实践</div>
            <p>中国中车构建了覆盖800+供应商的数字化协同平台，实现排产计划实时共享、来料质量在线检验、物流轨迹全程追踪。成品库存周转天数从45天降至28天，供应商交付准时率从78%提升至95%，质量追溯时间从7天缩短至4小时。</p>
          </div>
        </section>

        <!-- 第五章 挑战四 -->
        <section id="section5" class="chapter">
          <h2>五、挑战四：人才缺口与组织惯性</h2>

          <div class="challenge-banner">
            <div class="challenge-number">04</div>
            <div>
              <h3>制造业数字化人才的"结构性短缺"比总量短缺更严峻</h3>
              <p class="challenge-stat">76% 的制造业企业表示"找不到既懂制造又懂数据的人"</p>
            </div>
          </div>

          <h3>问题剖析</h3>
          <div class="talent-gap">
            <div class="talent-card">
              <h4>缺什么人？</h4>
              <div class="talent-list">
                <div class="talent-item" v-for="item in talentGaps" :key="item.role">
                  <div class="talent-role">{{ item.role }}</div>
                  <div class="talent-bar-track">
                    <div class="talent-bar-fill" :style="{ width: item.gap + '%' }"></div>
                  </div>
                  <span class="talent-gap-label">缺口 {{ item.gap }}%</span>
                </div>
              </div>
            </div>
            <div class="talent-card">
              <h4>组织惯性表现</h4>
              <ul class="org-inertia">
                <li>
                  <span class="inertia-icon">🔴</span>
                  <div>
                    <strong>"这不是我的事"</strong>
                    <p>数字化被视为IT部门的项目，车间主任和工艺工程师参与度低</p>
                  </div>
                </li>
                <li>
                  <span class="inertia-icon">🔴</span>
                  <div>
                    <strong>"以前一直这么做的"</strong>
                    <p>作业流程变革受到现场人员抵触，新系统上线后继续用老办法</p>
                  </div>
                </li>
                <li>
                  <span class="inertia-icon">🔴</span>
                  <div>
                    <strong>"系统不好用"</strong>
                    <p>系统设计未考虑现场操作场景，工人戴手套无法操作触屏等</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <h3>破局之道</h3>
          <div class="pillar-section">
            <div class="pillar-card">
              <div class="pillar-icon">🎓</div>
              <h4>分层培养体系</h4>
              <p>高中基三层差异化能力建设</p>
              <ul>
                <li>高层：数字化战略思维研修</li>
                <li>中层：数据分析与决策能力</li>
                <li>基层：数字化工具操作能力</li>
              </ul>
            </div>
            <div class="pillar-card">
              <div class="pillar-icon">🤝</div>
              <h4>"种子用户"策略</h4>
              <p>在每个产线培养数字化火种</p>
              <ul>
                <li>选拔一线数字化标兵</li>
                <li>赋予技术+管理双通道</li>
                <li>通过标杆带动全员转变</li>
              </ul>
            </div>
            <div class="pillar-card">
              <div class="pillar-icon">🔧</div>
              <h4>工具场景化设计</h4>
              <p>为车间场景定制交互方式</p>
              <ul>
                <li>工业化大屏代替PC操作</li>
                <li>语音交互代替键盘输入</li>
                <li>扫码报工代替纸质填写</li>
              </ul>
            </div>
          </div>
        </section>

        <!-- 第六章 挑战五 -->
        <section id="section6" class="chapter">
          <h2>六、挑战五：投入产出难以量化</h2>

          <div class="challenge-banner">
            <div class="challenge-number">05</div>
            <div>
              <h3>制造业数字化的ROI计算是全行业公认的难题</h3>
              <p class="challenge-stat">60% 的CFO无法清晰回答"数字化到底带来了多少回报"</p>
            </div>
          </div>

          <h3>问题剖析</h3>
          <p>制造业数字化转型的价值往往是"系统性"的、"长周期"的、"难以归因"的。具体表现为：</p>

          <div class="roi-issues">
            <div class="roi-card" v-for="item in roiIssues" :key="item.title">
              <div class="roi-icon">{{ item.icon }}</div>
              <h4>{{ item.title }}</h4>
              <p>{{ item.desc }}</p>
            </div>
          </div>

          <h3>破局之道：四步量化法</h3>
          <div class="quant-steps">
            <div class="quant-step" v-for="(step, i) in quantSteps" :key="i">
              <div class="quant-marker" :style="{ background: step.color }">{{ i + 1 }}</div>
              <div class="quant-content">
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
                <div class="quant-example">{{ step.example }}</div>
              </div>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">💡</div>
            <div>
              <h4>关键洞察：用"业务语言"而非"技术语言"沟通价值</h4>
              <p>不要说"我们上线了IoT平台"，而要说"设备非计划停机减少65%，相当于每年多出产能价值1.2亿元"。数字化团队需要学会用CFO能听懂的语言汇报成果。</p>
            </div>
          </div>
        </section>

        <!-- 第七章 -->
        <section id="section7" class="chapter">
          <h2>七、标杆案例与方法论总结</h2>

          <h3>7.1 三大标杆案例核心指标</h3>
          <div class="comparison-table">
            <table>
              <thead>
                <tr>
                  <th>维度</th>
                  <th>一汽集团</th>
                  <th>中船重工</th>
                  <th>中国中车</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>转型侧重</td>
                  <td>智能产线 + OT/IT融合</td>
                  <td>数据治理 + 质量追溯</td>
                  <td>供应链协同 + 智能制造</td>
                </tr>
                <tr>
                  <td>核心成果</td>
                  <td>OEE提升18%</td>
                  <td>交付周期 ↓15%</td>
                  <td>库存周转 ↓38%</td>
                </tr>
                <tr>
                  <td>数据治理</td>
                  <td>DCMM 3级</td>
                  <td>DCMM 4级</td>
                  <td>DCMM 3级</td>
                </tr>
                <tr>
                  <td>年化降本</td>
                  <td>12亿+</td>
                  <td>8亿+</td>
                  <td>15亿+</td>
                </tr>
                <tr>
                  <td>关键教训</td>
                  <td>设备协议统一先行</td>
                  <td>主数据治理是基础</td>
                  <td>供应商共赢生态</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>7.2 制造业数字化转型五步法</h3>
          <div class="methodology">
            <div class="method-step-card" v-for="(step, i) in methodology" :key="i">
              <div class="method-step-number" :style="{ background: step.color }">{{ i + 1 }}</div>
              <h4>{{ step.title }}</h4>
              <p>{{ step.desc }}</p>
              <div class="method-duration">⏱ {{ step.duration }}</div>
            </div>
          </div>

          <div class="visual-block blue-gradient" style="margin-top: 32px;">
            <div class="visual-block-inner">
              <div class="visual-icon">📝</div>
              <div>
                <h4>结语</h4>
                <p>制造业数字化转型不是"弯道超车"，而是"换道长跑"。五大挑战不会一夜之间消失，但只要坚持"场景驱动、数据为基、人才先行、价值量化"的原则，就能在长跑中持续领先。每一个小的突破，都是通向智能制造的坚实一步。</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- CTA -->
      <section class="cta-section">
        <h2>定制您的制造业数字化转型方案</h2>
        <p>古杉数据深耕制造业数据治理与智能审计，助力央企制造集团数字化突围</p>
        <div class="cta-buttons">
          <router-link to="/appointment" class="btn-primary">预约行业专家</router-link>
          <router-link to="/industry" class="btn-outline">查看行业方案</router-link>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
export default {
  name: 'ManufacturingChallenges',
  data() {
    return {
      otItSolutions: [
        { title: '引入工业互联网平台作为桥梁', desc: '通过统一的工业互联网平台（如工业数据采集网关）屏蔽底层协议差异，实现设备数据的标准化采集和上传。' },
        { title: '建立OT/IT融合组织', desc: '成立跨职能的"智能制造中心"，让自动化工程师和软件工程师在一个团队中工作，打破部门壁垒。' },
        { title: '分域分层安全架构', desc: '建立独立的工业安全体系，OT网络与IT网络物理隔离+逻辑互通，部署工业防火墙和入侵检测系统。' },
        { title: '先做"数据上云"再做"智能应用"', desc: '第一步只做数据采集和可视化（看得见），第二步再做分析和优化（管得好），降低实施风险。' }
      ],
      dataQualityIssues: [
        { icon: '❌', title: '编码不统一', desc: '同一物料在PLM、ERP、MES中有不同编码，无法关联', color: '#DC2626', impact: 'BOM准确率不足70%' },
        { icon: '⏰', title: '数据时效性差', desc: '生产报工延迟，库存数据与实物偏差大', color: '#EA580C', impact: '库存准确率仅75%' },
        { icon: '📉', title: '缺失值严重', desc: '设备运行数据采集覆盖率不足，关键工艺参数缺失', color: '#CA8A04', impact: 'AI模型无法训练' },
        { icon: '🔀', title: '口径不一致', desc: '不同部门对"合格率""OEE"等指标的计算口径不同', color: '#2563EB', impact: '管理层决策失据' }
      ],
      supplySolutions: [
        { title: 'AI需求预测', desc: '基于机器学习的多维度需求预测模型，整合历史销量、市场趋势、季节性因素，将预测准确率从65%提升至88%。' },
        { title: '供应商数字化协同平台', desc: '建设覆盖核心供应商的协同门户，实现采购订单电子化、排产计划共享、来料检验在线化、物流全程追踪。' },
        { title: '全链条质量追溯', desc: '基于一物一码和工序数据采集，建立从原材料到成品的正向追溯和逆向追溯能力，召回范围精确到批次。' },
        { title: '智能库存优化', desc: '基于安全库存模型+ABC分类+需求预测，动态调整各物料的库存水位，降低库存资金占用30%以上。' }
      ],
      talentGaps: [
        { role: '工业数据工程师', gap: 85 },
        { role: 'OT/IT融合架构师', gap: 80 },
        { role: '智能制造项目经理', gap: 72 },
        { role: 'AI算法工程师（工业）', gap: 68 },
        { role: '数据治理专家', gap: 65 }
      ],
      roiIssues: [
        { icon: '🕐', title: '价值滞后', desc: '数字化投入的效果通常需要12-24个月才能体现，短期内报表上看不到回报。' },
        { icon: '🔄', title: '难以归因', desc: 'OEE提升到底是因为新设备还是因为MES系统？多因素交织使得数字化的贡献无法单独剥离。' },
        { icon: '📊', title: '隐性价值多', desc: '数据透明度提升、决策速度加快、员工满意度改善等"软收益"难以货币化衡量。' },
        { icon: '💰', title: '投入分散', desc: '数字化费用散落在IT预算、设备改造、培训等多个科目中，全口径成本难以统计。' }
      ],
      quantSteps: [
        { title: '明确价值假设', desc: '在项目启动前，与业务部门共同定义可量化的价值目标。', example: '示例：OEE从72%提升至82%，对应年产值增加X万元', color: '#4A90D9' },
        { title: '建立基线数据', desc: '在项目实施前采集3-6个月的基线数据，作为对比基准。', example: '示例：记录当前平均停机时间、库存水位、质量合格率', color: '#1F6FEB' },
        { title: '设置对照组', desc: '在部分产线先行试点，与未改造产线对比，排除其他干扰因素。', example: '示例：A线部署AI质检 vs B线人工质检，3个月后对比', color: '#0E4EAD' },
        { title: '持续跟踪与归因', desc: '建立数字化价值看板，按月跟踪核心指标，分析变化归因。', example: '示例：每月生成《数字化价值报告》呈报管理层', color: '#0E1F52' }
      ],
      methodology: [
        { title: '产线诊断', desc: '对标行业标杆，评估数字化成熟度，识别高价值改进点', color: '#4A90D9', duration: '1-2个月' },
        { title: '顶层规划', desc: '制定3年数字化路线图，明确技术架构、数据架构、投资预算', color: '#1F6FEB', duration: '2-3个月' },
        { title: '标杆突破', desc: '选择1-2条产线率先突破，快速验证价值', color: '#0E4EAD', duration: '4-6个月' },
        { title: '规模复制', desc: '将成功模式复制到更多产线和工厂', color: '#0A3A82', duration: '6-12个月' },
        { title: '持续优化', desc: '建立运营体系，持续迭代优化，引入AI深度应用', color: '#0E1F52', duration: '持续' }
      ]
    }
  }
}
</script>

<style scoped>
.article-page { background: #fff; min-height: 100vh; }

.breadcrumb-nav { max-width: 960px; margin: 0 auto; padding: 20px 24px; font-size: 14px; color: #5A5E66; }
.breadcrumb-nav a { color: #1A73E8; text-decoration: none; }
.breadcrumb-nav a:hover { text-decoration: underline; }
.breadcrumb-nav .separator { margin: 0 8px; color: #ccc; }
.breadcrumb-nav .current { color: #0E1F52; }

.container { max-width: 960px; margin: 0 auto; padding: 0 24px 60px; }

.article-header { text-align: center; padding: 40px 0 32px; border-bottom: 1px solid #E8ECF0; margin-bottom: 40px; }
.article-header .article-tags { display: flex; justify-content: center; gap: 8px; margin-bottom: 20px; }
.article-header .tag { background: #EBF3FE; color: #1A73E8; padding: 4px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; }
.article-title { font-size: 2.2rem; font-weight: 800; color: #0E1F52; line-height: 1.3; margin-bottom: 16px; }
.article-subtitle { font-size: 1.1rem; color: #5A5E66; margin-bottom: 20px; }
.article-meta { display: flex; justify-content: center; gap: 24px; font-size: 14px; color: #8C8C8C; }
.article-meta i { margin-right: 4px; }

.intro-card { display: flex; gap: 16px; background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-left: 4px solid #1A73E8; border-radius: 12px; padding: 24px; margin-bottom: 40px; }
.intro-card .intro-icon { font-size: 2rem; flex-shrink: 0; }
.intro-card h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.intro-card p { font-size: 15px; color: #3D4150; line-height: 1.7; }

.toc { background: #F6F8FC; border-radius: 12px; padding: 24px 28px; margin-bottom: 48px; }
.toc h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.toc ul { list-style: none; padding: 0; }
.toc li { margin-bottom: 8px; }
.toc a { color: #1A73E8; text-decoration: none; font-size: 15px; }
.toc a:hover { color: #0E1F52; text-decoration: underline; }

.article-body { font-size: 16px; line-height: 1.85; color: #3D4150; }
.article-body p { margin-bottom: 18px; }
.chapter { margin-bottom: 56px; }
.chapter h2 { font-size: 1.6rem; font-weight: 800; color: #0E1F52; margin-bottom: 24px; padding-bottom: 12px; border-bottom: 3px solid #1A73E8; display: inline-block; }
.chapter h3 { font-size: 1.3rem; font-weight: 700; color: #0E1F52; margin: 28px 0 16px; }

.visual-block { border-radius: 12px; padding: 24px; margin: 24px 0; }
.blue-gradient { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; }
.visual-block-inner { display: flex; gap: 16px; align-items: center; }
.visual-icon { font-size: 2.5rem; flex-shrink: 0; }
.visual-block h4 { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.visual-block p { font-size: 15px; line-height: 1.6; opacity: 0.9; margin: 0; }

.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 28px 0; }
.stat-card { background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-radius: 12px; padding: 24px; text-align: center; border: 1px solid #D6E4F0; }
.stat-number { font-size: 2.2rem; font-weight: 900; color: #1A73E8; margin-bottom: 8px; }
.stat-label { font-size: 13px; color: #5A5E66; line-height: 1.5; }

.comparison-table { margin: 28px 0; overflow-x: auto; }
.comparison-table h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.comparison-table table { width: 100%; border-collapse: collapse; font-size: 14px; }
.comparison-table th { background: #0E1F52; color: #fff; padding: 12px 16px; text-align: left; font-weight: 600; }
.comparison-table td { padding: 12px 16px; border-bottom: 1px solid #E8ECF0; color: #3D4150; }
.comparison-table tr:nth-child(even) td { background: #F6F8FC; }

/* 挑战横幅 */
.challenge-banner { display: flex; gap: 20px; align-items: center; background: linear-gradient(135deg, #FEF2F2 0%, #FFF5F5 100%); border-left: 4px solid #DC2626; border-radius: 12px; padding: 24px; margin: 24px 0; }
.challenge-number { font-size: 2.5rem; font-weight: 900; color: #DC2626; flex-shrink: 0; line-height: 1; }
.challenge-banner h3 { font-size: 18px; font-weight: 700; color: #0E1F52; margin: 0 0 6px; }
.challenge-stat { font-size: 14px; color: #DC2626; font-weight: 600; margin: 0 !important; }

/* 问题卡片 */
.problem-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin: 20px 0; }
.problem-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.problem-icon { font-size: 1.5rem; margin-bottom: 8px; }
.problem-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.problem-card p { font-size: 13px; color: #5A5E66; line-height: 1.6; margin: 0; }

/* 解决步骤 */
.solution-steps { margin: 24px 0; position: relative; padding-left: 40px; }
.solution-steps::before { content: ''; position: absolute; left: 19px; top: 0; bottom: 0; width: 3px; background: linear-gradient(to bottom, #52C41A, #059669); border-radius: 2px; }
.solution-step { position: relative; margin-bottom: 20px; }
.solution-step:last-child { margin-bottom: 0; }
.step-marker { position: absolute; left: -40px; width: 38px; height: 38px; background: #52C41A; border-radius: 50%; color: #fff; font-size: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; z-index: 1; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
.step-content { background: #F0FFF4; border-radius: 12px; padding: 20px; border: 1px solid #D1FAE5; }
.step-content h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.step-content p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin: 0; }

/* 案例小卡 */
.case-mini { background: #EBF3FE; border-radius: 12px; padding: 20px; margin: 24px 0; border: 1px solid #D6E4F0; }
.case-mini-header { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.case-mini p { font-size: 14px; color: #3D4150; line-height: 1.7; margin: 0; }

/* 数据孤岛图 */
.island-diagram { margin: 24px 0; }
.island-layer { background: #F6F8FC; border-radius: 12px; padding: 16px 24px; border: 1px solid #E8ECF0; text-align: center; }
.island-title { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.island-systems { display: flex; justify-content: center; gap: 8px; flex-wrap: wrap; }
.island-systems span { background: #0E1F52; color: #fff; padding: 6px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; }
.island-gap { text-align: center; padding: 8px 0; }
.gap-label { font-size: 13px; color: #DC2626; font-weight: 600; }

/* 数据质量 */
.quality-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin: 20px 0; }
.quality-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.quality-icon { font-size: 1.5rem; margin-bottom: 8px; }
.quality-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.quality-card p { font-size: 13px; color: #5A5E66; line-height: 1.5; margin-bottom: 8px; }
.quality-impact { font-size: 12px; color: #DC2626; font-weight: 600; }

/* 支柱 */
.pillar-section { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.pillar-card { background: #F6F8FC; border-radius: 12px; padding: 24px; border: 1px solid #E8ECF0; transition: all 0.3s; }
.pillar-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(31,63,143,0.1); }
.pillar-icon { font-size: 2rem; margin-bottom: 12px; }
.pillar-card h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.pillar-card > p { font-size: 14px; color: #5A5E66; margin-bottom: 12px; }
.pillar-card ul { list-style: none; padding: 0; margin: 0; }
.pillar-card li { font-size: 13px; color: #5A5E66; padding: 3px 0 3px 14px; position: relative; }
.pillar-card li::before { content: '▸'; position: absolute; left: 0; color: #1A73E8; font-weight: 700; }

/* 痛点 */
.pain-points { margin: 24px 0; }
.pain-card { border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; margin-bottom: 16px; }
.pain-header { padding: 12px 20px; color: #fff; }
.pain-header.red { background: linear-gradient(135deg, #DC2626, #991B1B); }
.pain-header.orange { background: linear-gradient(135deg, #EA580C, #9A3412); }
.pain-header.yellow { background: linear-gradient(135deg, #CA8A04, #854D0E); }
.pain-header span { font-size: 14px; font-weight: 700; }
.pain-body { padding: 20px; }
.pain-body h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.pain-body p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin: 0; }

/* 人才缺口 */
.talent-gap { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin: 24px 0; }
.talent-card { background: #F6F8FC; border-radius: 12px; padding: 24px; border: 1px solid #E8ECF0; }
.talent-card h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 16px; }
.talent-list { display: flex; flex-direction: column; gap: 12px; }
.talent-item { display: flex; align-items: center; gap: 12px; }
.talent-role { font-size: 13px; color: #3D4150; width: 150px; flex-shrink: 0; font-weight: 500; }
.talent-bar-track { flex: 1; height: 10px; background: #E8ECF0; border-radius: 5px; overflow: hidden; }
.talent-bar-fill { height: 100%; background: linear-gradient(90deg, #DC2626, #EA580C); border-radius: 5px; }
.talent-gap-label { font-size: 12px; color: #DC2626; font-weight: 600; width: 60px; text-align: right; flex-shrink: 0; }
.org-inertia { list-style: none; padding: 0; margin: 0; }
.org-inertia li { display: flex; gap: 12px; margin-bottom: 16px; align-items: flex-start; }
.org-inertia li:last-child { margin-bottom: 0; }
.inertia-icon { font-size: 1.2rem; flex-shrink: 0; margin-top: 2px; }
.org-inertia strong { display: block; font-size: 14px; color: #0E1F52; margin-bottom: 4px; }
.org-inertia p { font-size: 13px; color: #5A5E66; line-height: 1.5; margin: 0; }

/* ROI */
.roi-issues { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin: 24px 0; }
.roi-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.roi-icon { font-size: 1.5rem; margin-bottom: 8px; }
.roi-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.roi-card p { font-size: 13px; color: #5A5E66; line-height: 1.6; margin: 0; }

/* 量化步骤 */
.quant-steps { margin: 24px 0; position: relative; padding-left: 40px; }
.quant-steps::before { content: ''; position: absolute; left: 19px; top: 0; bottom: 0; width: 3px; background: linear-gradient(to bottom, #4A90D9, #0E1F52); border-radius: 2px; }
.quant-step { position: relative; margin-bottom: 24px; }
.quant-step:last-child { margin-bottom: 0; }
.quant-marker { position: absolute; left: -40px; width: 38px; height: 38px; border-radius: 50%; color: #fff; font-size: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; z-index: 1; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
.quant-content { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.quant-content h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.quant-content p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin-bottom: 8px; }
.quant-example { font-size: 13px; color: #1A73E8; font-weight: 500; background: #EBF3FE; padding: 8px 14px; border-radius: 8px; }

/* 高亮 */
.highlight-box { display: flex; gap: 16px; background: #FFF7E6; border-left: 4px solid #FAAD14; border-radius: 12px; padding: 24px; margin: 28px 0; }
.highlight-icon { font-size: 2rem; flex-shrink: 0; }
.highlight-box h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.highlight-box p { font-size: 15px; color: #3D4150; line-height: 1.7; margin: 0; }

/* 方法论 */
.methodology { display: flex; gap: 8px; margin: 24px 0; }
.method-step-card { flex: 1; background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; text-align: center; }
.method-step-number { width: 36px; height: 36px; border-radius: 50%; color: #fff; font-size: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; margin: 0 auto 12px; }
.method-step-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.method-step-card p { font-size: 12px; color: #5A5E66; line-height: 1.5; margin-bottom: 8px; }
.method-duration { font-size: 12px; color: #1A73E8; font-weight: 600; }

/* CTA */
.cta-section { text-align: center; background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; border-radius: 16px; padding: 48px 32px; margin-bottom: 60px; }
.cta-section h2 { font-size: 1.8rem; font-weight: 800; margin-bottom: 12px; }
.cta-section p { font-size: 16px; opacity: 0.85; margin-bottom: 28px; }
.cta-buttons { display: flex; justify-content: center; gap: 16px; }
.btn-primary { background: #1A73E8; color: #fff; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; transition: all 0.3s; }
.btn-primary:hover { background: #1557B0; }
.btn-outline { background: transparent; color: #fff; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; border: 2px solid rgba(255,255,255,0.5); transition: all 0.3s; }
.btn-outline:hover { border-color: #fff; background: rgba(255,255,255,0.1); }

@media (max-width: 768px) {
  .article-title { font-size: 1.6rem; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .problem-grid { grid-template-columns: 1fr; }
  .pillar-section { grid-template-columns: 1fr; }
  .quality-grid { grid-template-columns: 1fr; }
  .talent-gap { grid-template-columns: 1fr; }
  .roi-issues { grid-template-columns: 1fr; }
  .methodology { flex-direction: column; }
  .article-meta { flex-wrap: wrap; justify-content: center; }
  .cta-buttons { flex-direction: column; align-items: center; }
  .challenge-banner { flex-direction: column; text-align: center; }
}
@media (max-width: 480px) {
  .stats-grid { grid-template-columns: 1fr; }
}
</style>
